import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getRestaurantInfo, API_ORIGIN } from '../../services/api';
import { calculateStatus } from './utils/calculateStatus';
import HeroSection from './HeroSection';
import DescriptionCard from './DescriptionCard';
import PhotoGallery from './PhotoGallery';
import OpeningHours from './OpeningHours';
import ContactCards from './ContactCards';
import MapSection from './MapSection';
import SocialLinks from './SocialLinks';
import EmptyState from './EmptyState';
import SkeletonLoader from './SkeletonLoader';
import './RestaurantInfo.css';

export default function RestaurantInfo() {
  const { t } = useTranslation();
  const { restaurantId } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getImageUrl = useCallback((url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_ORIGIN}${url}`;
  }, []);

  const hasAnyContent = useCallback((data) => {
    if (!data) return false;
    const hasText = (data.description && data.description.trim()) ||
      (data.address && data.address.trim()) ||
      (data.phone && data.phone.trim()) ||
      (data.email && data.email.trim()) ||
      (data.website && data.website.trim());
    const hasPhotos = Array.isArray(data.photos) && data.photos.length > 0;
    const hasSocial = data.social_media && typeof data.social_media === 'object' &&
      (data.social_media.facebook || data.social_media.instagram || data.social_media.twitter);
    return !!hasText || hasPhotos || hasSocial;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getRestaurantInfo(restaurantId);
        if (!cancelled) setInfo(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || t('api.errors.fetchRestaurantInfo'));
          setInfo(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchInfo();
    return () => { cancelled = true; };
  }, [restaurantId, t]);

  if (loading) return <SkeletonLoader />;

  if (error) {
    return (
      <div className="ri-page">
        <div className="ri-error-state">
          <p className="ri-error-message">{error}</p>
          <Link to={`/menu/${restaurantId}`} className="ri-btn ri-btn-primary">
            {t('restaurant.info.backToMenu')}
          </Link>
        </div>
      </div>
    );
  }

  const isEmpty = !hasAnyContent(info);
  const openingHours = info.opening_hours && typeof info.opening_hours === 'object' ? info.opening_hours : {};
  const photos = Array.isArray(info.photos) ? info.photos : [];
  const statusBanner = Object.keys(openingHours).length > 0 ? calculateStatus(openingHours) : null;
  const heroImageUrl = photos.length > 0 ? getImageUrl(photos[0]) : null;

  if (isEmpty) {
    return (
      <div className="ri-page">
        <HeroSection
          restaurantId={restaurantId}
          restaurantName={info.restaurantName || 'Restaurant'}
          heroImageUrl={null}
          statusBanner={null}
        />
        <main className="ri-main">
          <EmptyState restaurantId={restaurantId} />
        </main>
      </div>
    );
  }

  return (
    <div className="ri-page">
      <HeroSection
        restaurantId={restaurantId}
        restaurantName={info.restaurantName || 'Restaurant'}
        heroImageUrl={heroImageUrl}
        statusBanner={statusBanner}
      />
      <main className="ri-main">
        <div className="ri-content">
          <DescriptionCard description={info.description} />
          <PhotoGallery photos={photos} getImageUrl={getImageUrl} />
          <OpeningHours openingHours={openingHours} />
          <ContactCards
            address={info.address}
            phone={info.phone}
            email={info.email}
            website={info.website}
          />
          {info.address && <MapSection address={info.address} />}
          <SocialLinks social_media={info.social_media} />
        </div>
      </main>
    </div>
  );
}
